import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link } from 'gatsby'
import { navigate } from 'gatsby-plugin-intl'
import { useSwipeable } from 'react-swipeable'

import PauseIcon from '@icons/Pause'
import PlayIcon from '@icons/Play'
import VolumeIcon from '@icons/Volume'
import Loading from '@atoms/Loading'
import ArrowLeft from '@icons/ArrowLeft'
import { Article } from '@models/Article'
import { useAllArticles } from '@models/Article/useAllArticles'
import { useAllEnglishArticles } from '@models/Article/useAllEnglishArticles'
import { StoreContext } from '@models/Store'
import { buildVoiceUrl, checkMaxAudioCount, Speed } from '@models/Voice'
import { sendEvent } from '@models/Analytics'
import { buildTargetLocalePath } from '@models/Translation'
import { getRecommendations } from '@models/Personalize'
import { sendError } from '@models/Sentry'
import { useScroll } from '@models/Util'

import playingBlackGif from './assets/playing-black.gif'

import * as style from './style.module.sass'

type AnalyticsData = {
  playId: string
  startTime: number
  prevTime: number | undefined
}

const VoicePlayer = () => {
  const { entity, action } = useContext(StoreContext)
  const { setVoiceIsPlaying } = action
  const { voice } = entity
  const isPlaying = voice?.isPlaying || false
  const { isTop, isScrollingDown } = useScroll()

  const [state, setState] = useState<'normal' | 'small' | 'mini'>('small')
  const handlers = useSwipeable({
    onSwipedDown: () => setState('small'),
    onSwipedUp: () => setState('normal'),
  })
  const playlistRef = useRef<HTMLDivElement>(null)
  const [dragStart, setDragStart] = useState<number | undefined>()

  const { pathname } = window.location
  const locale = pathname.startsWith('/en') ? 'en' : 'ja'
  const japanesseArticles = useAllArticles()
  const englishArticles = useAllEnglishArticles()
  const articles = locale === 'ja' ? japanesseArticles : englishArticles
  const articleMap = useMemo(() => {
    const map: { [id: string]: Article } = {}
    articles.forEach(article => (map[article.blogId] = article))
    return map
  }, [articles])

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.5)
  const [speed, setSpeed] = useState<Speed>('normal')
  const [showSpeedMenu, setShowSpeedMenu] = useState(false)
  const [isMute, setIsMute] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const playerRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const playArticles = voice.articleIds
    .map(id => articleMap[id])
    .filter(article => article !== undefined)
  const currentArticle = playArticles[voice.playIndex]
  const lastIndex = voice.articleIds.length - 1

  // for Analytics
  const [analyticsData, setAnalyticsData] = useState<
    AnalyticsData | undefined
  >()

  const playPauseHandler = () => {
    if (isPlaying) {
      setAnalyticsData(data => {
        if (data === undefined) {
          return undefined
        }

        return { ...data, startTime: Date.now() }
      })
    }
    setVoiceIsPlaying(!isPlaying)
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0')
    return `${minutes}:${seconds}`
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (state === 'mini') {
      setState('small')
    }

    const audio = audioRef.current
    if (audio === null) {
      return
    }

    const value = parseInt(event.target.value)
    const newTime = (value / 100) * duration
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const moveSeconds = (seconds: number) => {
    const audio = audioRef.current
    if (audio === null) {
      return
    }

    const newTime = Math.max(0, Math.min(audio.currentTime + seconds, duration))
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const onChageVolume = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (audio === null) {
      return
    }

    const value = parseInt(event.target.value) / 100
    audio.volume = value
    setVolume(value)
    setIsMute(false)
  }

  const onMute = () => {
    const audio = audioRef.current
    if (audio === null) {
      return
    }

    setIsMute(value => {
      if (value) {
        audio.volume = volume
        return false
      }

      audio.volume = 0
      return true
    })
  }

  const changeIndex = (index: number) => {
    if (index === voice.playIndex) {
      return
    }

    if (entity.user?.isSignIn !== true) {
      if (checkMaxAudioCount()) {
        const targetArticle = articleMap[voice.articleIds[index]]
        navigate(`/signup?limit=voice&redirect=${targetArticle.path}`)
        return
      }
    }

    setCurrentTime(0)
    action.setVoicePlayIndex(Math.min(Math.max(index, 0), lastIndex))
  }

  const onEnd = () => {
    if (voice.playIndex === voice.articleIds.length - 1) {
      setVoiceIsPlaying(false)
      return
    }

    setTimeout(() => {
      changeIndex(voice.playIndex + 1)
    }, 3000)
  }

  const close = () => {
    setAnalyticsData(undefined)
    action.setVoiceArticleIds([])
  }

  const updateProgress = () => {
    const audio = audioRef.current
    if (audio === null || audio.duration === 0) {
      return
    }

    if (audio.currentTime !== 0) {
      setCurrentTime(audio.currentTime)
      setDuration(audio.duration)
    }

    setAnalyticsData(data => {
      if (data === undefined || voice === undefined) {
        return undefined
      }

      const currentSeconds = Math.floor(audio.currentTime)
      // 5秒ごとにイベントを送信
      if (currentSeconds % 5 === 0 && currentSeconds !== data.prevTime) {
        sendEvent({
          name: 'play_voice',
          param: {
            play_id: data.playId,
            article_id: currentArticle.blogId,
            start_time: data.startTime,
            audio_time: currentSeconds,
            max_audio_time: Math.floor(audio.duration),
          },
        })
        return { ...data, prevTime: currentSeconds }
      }

      return data
    })
  }

  const changeSpeed = (targetSpeed: Speed) => {
    if (speed === targetSpeed) {
      return
    }

    let newTime = currentTime
    // normalのスピードの時間に戻す
    if (speed === 'fast') {
      newTime *= 1.2
    }
    if (speed === 'x-fast') {
      newTime *= 1.5
    }
    // 新しいスピードの時間に戻す
    if (targetSpeed === 'fast') {
      newTime /= 1.2
    }
    if (targetSpeed === 'x-fast') {
      newTime /= 1.5
    }

    setShowSpeedMenu(false)
    setSpeed(targetSpeed)
    setTimeout(() => {
      const audio = audioRef.current
      if (audio === null) {
        return
      }
      audio.currentTime = newTime
      if (isPlaying) {
        audio.play()
      }
    }, 500)
  }

  const onDrag = (clientY: number) => {
    if (dragStart === undefined) {
      return
    }

    const diff = dragStart - clientY
    if (Math.abs(diff) > 10) {
      setState(diff > 0 ? 'normal' : 'mini')
    }
  }

  const onWheel = (event: WheelEvent) => {
    event.preventDefault()
    setState(event.deltaY > 0 ? 'normal' : 'mini')
  }

  const disableScroll = (event: globalThis.TouchEvent) => event.preventDefault()

  useEffect(() => {
    if (voice.articleIds.length === 0 && voice.context === 'latest') {
      action.setVoiceArticleIds(
        articles
          .slice(0, 5)
          .filter(article => article.enableVoice)
          .filter(filterMask)
          .map(article => article.blogId),
        'latest',
      )
      return
    }

    const audio = audioRef.current
    if (audio === null) {
      return
    }
    audio.volume = isMute ? 0 : volume

    if (voice === undefined || !voice.isPlaying) {
      audio.pause()
      return
    }

    setTimeout(() => {
      audio.play()
      playlistRef.current?.scrollTo({
        top: Math.max((voice.playIndex - 1) * 81, 0),
        behavior: 'smooth',
      })

      if (analyticsData === undefined) {
        const now = Date.now()
        setAnalyticsData({
          playId: `${entity.user?.userId}_${currentArticle.blogId}_${now}`,
          startTime: now,
          prevTime: undefined,
        })
      }
    }, 500)

    if (voice.playIndex === lastIndex && voice.context === 'latest') {
      action.setVoiceArticleIds(
        articles
          .slice(0, voice.articleIds.length + 5)
          .filter(article => article.enableVoice)
          .filter(filterMask)
          .map(article => article.blogId),
        'latest',
        true,
      )
      return
    }

    if (voice.playIndex === lastIndex) {
      getRecommendations(voice.articleIds[lastIndex], 10)
        .then(data => {
          if (data === undefined) {
            return
          }
          const newArticleIds = data.articleIds
            .filter(
              id =>
                !voice.articleIds.includes(id) && articleMap[id].enableVoice,
            )
            .filter(articleId => filterMask(articleMap[articleId]))
            .slice(0, lastIndex === 0 ? 4 : 5)
          action.setVoiceArticleIds(
            [...voice.articleIds, ...newArticleIds],
            undefined,
            true,
          )
        })
        .catch(error => {
          // 必須ではないのでひとまず握り潰す
          sendError(error)
        })
      return
    }
  }, [voice])

  useEffect(() => {
    playerRef.current?.addEventListener('touchmove', disableScroll)
    playerRef.current?.addEventListener('wheel', onWheel)

    return () => {
      playerRef.current?.addEventListener('touchmove', disableScroll)
      playerRef.current?.addEventListener('wheel', onWheel)
    }
  }, [playerRef, voice])

  useEffect(() => setIsLoading(true), [currentArticle])

  useEffect(() => {
    setState(!isTop && isScrollingDown ? 'mini' : 'small')
  }, [isTop, isScrollingDown])

  const filterMask = (article: Article) =>
    entity.user?.isSignIn === true || !article.isMembersOnly

  return (
    <div
      className={style.player}
      data-state={currentArticle === undefined ? 'hidden' : state}
    >
      {voice !== undefined && currentArticle !== undefined && (
        <>
          <div
            {...handlers}
            ref={playerRef}
            onTouchStart={event => setDragStart(event.touches[0].clientY)}
            onMouseDown={event => setDragStart(event.clientY)}
            onTouchMove={event => onDrag(event.touches[0].clientY)}
            onMouseMove={event => onDrag(event.clientY)}
            onTouchEnd={() => setDragStart(undefined)}
            onMouseUp={() => setDragStart(undefined)}
          >
            <button
              className={style.handle}
              onClick={() => setState(state === 'normal' ? 'mini' : 'normal')}
            />
            <button onClick={close} className={style.close}>
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                <rect
                  width="0.574237"
                  height="10.6234"
                  rx="0.287119"
                  transform="matrix(-0.710066 0.704135 -0.710066 -0.704135 8.75 7.85547)"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.625"
                />
                <rect
                  width="0.574236"
                  height="10.6234"
                  rx="0.287118"
                  transform="matrix(0.710065 0.704136 -0.710065 0.704136 8.29297 0.490234)"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.625"
                />
              </svg>
            </button>

            <Link
              to={buildTargetLocalePath(locale, currentArticle.path)}
              className={style.title}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M5.25 12.75L12.75 5.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.25 5.25L12.75 5.25V12.75"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div
                className={style.scroll}
                key={`${currentArticle.blogId}_${pathname}`}
              >
                <span
                  style={{
                    animationDuration: `${currentArticle.title.length / 2}s`,
                  }}
                >
                  {currentArticle.title}
                </span>
                <span
                  style={{
                    animationDuration: `${currentArticle.title.length / 2}s`,
                  }}
                >
                  {currentArticle.title}
                </span>
              </div>
            </Link>

            <div className={style.ui}>
              <button
                onClick={() => changeIndex(voice.playIndex - 1)}
                disabled={voice.playIndex === 0}
              >
                <svg width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <g clipPath="url(#clip0_15321_73618)">
                    <path
                      d="M0.625 4.87543C0.45924 4.87543 0.300269 4.94128 0.183058 5.05849C0.065848 5.1757 0 5.33467 0 5.50043L0 15.5004C0 15.6662 0.065848 15.8252 0.183058 15.9424C0.300269 16.0596 0.45924 16.1254 0.625 16.1254C0.79076 16.1254 0.949732 16.0596 1.06694 15.9424C1.18415 15.8252 1.25 15.6662 1.25 15.5004V11.4417L9.08375 15.9867C9.75875 16.3779 10.625 15.9042 10.625 15.1154V11.4404L18.4587 15.9854C19.1338 16.3779 20 15.9042 20 15.1154V5.88543C20 5.09793 19.1338 4.62293 18.4587 5.01543L10.625 9.56043V5.88543C10.625 5.09793 9.76 4.62293 9.08375 5.01543L1.25 9.56043V5.50043C1.25 5.33467 1.18415 5.1757 1.06694 5.05849C0.949732 4.94128 0.79076 4.87543 0.625 4.87543Z"
                      fill="#565656"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_15321_73618">
                      <rect
                        width="20"
                        height="20"
                        fill="white"
                        transform="translate(0 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </button>
              <button onClick={() => moveSeconds(-15)}>
                <svg width="36" height="35" viewBox="0 0 36 35" fill="none">
                  <path
                    d="M17.6234 28.4498C23.8119 28.4498 28.9198 23.3418 28.9198 17.1534C28.9198 11.7398 25.0015 7.15577 19.8827 6.09708V4.49266C19.8827 3.66316 19.2606 3.42305 18.5839 3.91419L14.8948 6.52274C14.3382 6.92657 14.3382 7.53778 14.8948 7.9307L18.5729 10.5502C19.2496 11.0413 19.8827 10.823 19.8827 9.97169V8.40002C23.79 9.39323 26.6496 12.9186 26.6496 17.1534C26.6496 22.1631 22.6331 26.1905 17.6234 26.1905C12.6137 26.1905 8.58626 22.1631 8.59717 17.1534C8.60809 14.2392 9.97239 11.6962 12.1116 10.0481C12.6246 9.6006 12.7883 8.95665 12.3954 8.42185C12.0352 7.90887 11.3258 7.82155 10.7364 8.30179C8.0842 10.2882 6.33789 13.628 6.33789 17.1534C6.33789 23.3418 11.4458 28.4498 17.6234 28.4498ZM14.5019 21.7265C14.993 21.7265 15.2986 21.3991 15.2986 20.8752V14.1083C15.2986 13.4534 14.9494 13.115 14.3709 13.115C14.0216 13.115 13.7597 13.2242 13.334 13.5407L11.9261 14.5448C11.6314 14.7849 11.5004 15.0032 11.5004 15.2979C11.5004 15.6908 11.8169 16.0183 12.1989 16.0183C12.45 16.0183 12.5918 15.9528 12.7992 15.7563L13.716 15.0141V20.8752C13.716 21.3882 14.0216 21.7265 14.5019 21.7265ZM19.9809 21.8357C21.8145 21.8357 23.0151 20.6569 23.0151 18.8451C23.0151 17.2189 21.9237 16.1056 20.4284 16.1056C19.7626 16.1056 19.0641 16.4221 18.7585 16.9351L18.9331 14.6431H22.0656C22.4585 14.6431 22.7532 14.3375 22.7532 13.9227C22.7532 13.508 22.4585 13.2351 22.0656 13.2351H18.5948C17.9945 13.2351 17.6561 13.5953 17.6125 14.2283L17.3942 17.3389C17.3396 17.9283 17.667 18.223 18.18 18.223C18.5839 18.223 18.7367 18.1466 19.0313 17.9174C19.4134 17.579 19.7299 17.4262 20.1555 17.4262C20.9632 17.4262 21.4871 18.0156 21.4871 18.8779C21.4871 19.7728 20.865 20.4277 20.0355 20.4277C19.4134 20.4277 18.9113 20.0784 18.6275 19.5545C18.4638 19.2926 18.2673 19.1398 17.9617 19.1398C17.5579 19.1398 17.2741 19.4236 17.2741 19.8383C17.2741 20.002 17.3069 20.1548 17.3724 20.3186C17.667 21.0716 18.6384 21.8357 19.9809 21.8357Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {isLoading ? (
                <div className={style.loadingContainer}>
                  <Loading />
                </div>
              ) : (
                <button
                  onClick={playPauseHandler}
                  className={style.playPause}
                  data-active={voice.isPlaying}
                >
                  {voice.isPlaying ? <PauseIcon /> : <PlayIcon />}
                </button>
              )}
              <button onClick={() => moveSeconds(15)}>
                <svg width="36" height="35" viewBox="0 0 36 35" fill="none">
                  <path
                    d="M18.1727 28.4509C24.3612 28.4509 29.4691 23.343 29.4691 17.1545C29.4691 13.6292 27.7119 10.2894 25.0706 8.30293C24.4812 7.82269 23.7609 7.91001 23.4007 8.42299C23.0078 8.95779 23.1824 9.60174 23.6845 10.0492C25.8346 11.6973 27.1989 14.2404 27.2098 17.1545C27.2208 22.1642 23.1824 26.1916 18.1727 26.1916C13.163 26.1916 9.1574 22.1642 9.1574 17.1545C9.1574 12.9197 12.017 9.39437 15.9243 8.40116V9.98375C15.9243 10.8242 16.5465 11.0534 17.2232 10.5622L20.9122 7.95367C21.4579 7.54983 21.4689 6.93863 20.9122 6.54571L17.2341 3.92625C16.5465 3.4351 15.9243 3.65339 15.9243 4.50471V6.09822C10.7946 7.15691 6.88721 11.741 6.88721 17.1545C6.88721 23.343 11.9952 28.4509 18.1727 28.4509ZM15.0512 21.7277C15.5423 21.7277 15.8479 21.4002 15.8479 20.8763V14.1094C15.8479 13.4545 15.4987 13.1162 14.9202 13.1162C14.571 13.1162 14.309 13.2253 13.8833 13.5418L12.4754 14.546C12.1807 14.7861 12.0497 15.0044 12.0497 15.2991C12.0497 15.692 12.3662 16.0194 12.7482 16.0194C12.9993 16.0194 13.1412 15.9539 13.3485 15.7575L14.2653 15.0153V20.8763C14.2653 21.3893 14.571 21.7277 15.0512 21.7277ZM20.5302 21.8368C22.3638 21.8368 23.5644 20.658 23.5644 18.8462C23.5644 17.22 22.473 16.1067 20.9777 16.1067C20.3119 16.1067 19.6134 16.4233 19.3078 16.9362L19.4824 14.6442H22.6149C23.0078 14.6442 23.3025 14.3386 23.3025 13.9239C23.3025 13.5091 23.0078 13.2362 22.6149 13.2362H19.1441C18.5438 13.2362 18.2055 13.5964 18.1618 14.2295L17.9435 17.3401C17.8889 17.9294 18.2164 18.2241 18.7293 18.2241C19.1332 18.2241 19.286 18.1477 19.5807 17.9185C19.9627 17.5802 20.2792 17.4274 20.7049 17.4274C21.5125 17.4274 22.0364 18.0168 22.0364 18.879C22.0364 19.774 21.4143 20.4288 20.5848 20.4288C19.9627 20.4288 19.4606 20.0796 19.1768 19.5557C19.0131 19.2937 18.8167 19.1409 18.5111 19.1409C18.1072 19.1409 17.8234 19.4247 17.8234 19.8395C17.8234 20.0032 17.8562 20.156 17.9217 20.3197C18.2164 21.0728 19.1877 21.8368 20.5302 21.8368Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <button
                onClick={() => changeIndex(voice.playIndex + 1)}
                disabled={voice.playIndex === voice.articleIds.length - 1}
              >
                <svg width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path
                    d="M19.375 4.87543C19.5408 4.87543 19.6997 4.94128 19.8169 5.05849C19.9342 5.1757 20 5.33467 20 5.50043V15.5004C20 15.6662 19.9342 15.8252 19.8169 15.9424C19.6997 16.0596 19.5408 16.1254 19.375 16.1254C19.2092 16.1254 19.0503 16.0596 18.9331 15.9424C18.8158 15.8252 18.75 15.6662 18.75 15.5004V11.4417L10.9163 15.9867C10.2413 16.3779 9.375 15.9042 9.375 15.1154V11.4404L1.54125 15.9854C0.86625 16.3792 0 15.9054 0 15.1167V5.88543C0 5.09794 0.86625 4.62293 1.54125 5.01543L9.375 9.56043V5.88543C9.375 5.09794 10.2413 4.62293 10.9163 5.01543L18.75 9.56043V5.50043C18.75 5.33467 18.8158 5.1757 18.9331 5.05849C19.0503 4.94128 19.2092 4.87543 19.375 4.87543Z"
                    fill="#434343"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className={style.progress}>
            <div className={style.time}>{formatTime(currentTime)}</div>
            <div className={style.bar}>
              <div
                className={style.value}
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <input
                className={style.slider}
                type="range"
                min="0"
                max="100"
                value={duration ? (currentTime / duration) * 100 : 0}
                onChange={onChange}
                onMouseDown={() => setVoiceIsPlaying(false)}
                onTouchStart={() => setVoiceIsPlaying(false)}
                onMouseUp={() => setVoiceIsPlaying(true)}
                onTouchEnd={() => setVoiceIsPlaying(true)}
              />
            </div>
            <div className={style.time}>
              -{formatTime(duration - currentTime)}
            </div>
          </div>

          <div className={style.footer}>
            <div className={style.volume}>
              <button onClick={onMute}>
                <VolumeIcon isMute={isMute} />
              </button>
              <div className={style.bar}>
                <div
                  className={style.value}
                  style={{ width: `${isMute ? 0 : volume * 50}px` }}
                />
                <input
                  className={style.slider}
                  type="range"
                  min="0"
                  max="100"
                  value={isMute ? 0 : volume * 100}
                  onChange={onChageVolume}
                />
              </div>
            </div>

            <button
              className={style.next}
              onClick={() => setState(state === 'normal' ? 'small' : 'normal')}
            >
              <div className={style.text}>
                {locale === 'ja' ? '次' : 'Next'}：
                {playArticles[voice.playIndex + 1]?.title || '-'}
              </div>
              <ArrowLeft />
            </button>

            <div className={style.speed}>
              <button
                className={style.status}
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              >
                <svg width="28" height="29" viewBox="0 0 28 29" fill="none">
                  <g clipPath="url(#clip0_15218_75146)">
                    <path
                      d="M11.5828 28.1448C9.56331 27.8025 7.61226 26.9924 5.93504 25.8058C5.91222 25.7944 5.8894 25.7602 5.8894 25.7374C5.8894 25.7032 5.8894 25.6803 5.91222 25.6575L6.69949 24.5964C6.69949 24.5964 6.75654 24.5508 6.79077 24.5508C6.81359 24.5508 6.8364 24.5508 6.85922 24.5736C8.30825 25.5891 9.99688 26.2965 11.7426 26.6159C11.7996 26.6159 11.8338 26.673 11.8338 26.73L11.7083 28.0535C11.7083 28.0535 11.6855 28.1106 11.6627 28.1334C11.6399 28.1448 11.617 28.1562 11.5942 28.1562C11.5942 28.1562 11.5828 28.1562 11.5714 28.1562L11.5828 28.1448Z"
                      fill="#565656"
                    />
                    <path
                      d="M6.79091 24.6543C8.25135 25.6811 9.95139 26.3885 11.7199 26.708L11.5944 28.0315C9.58628 27.6892 7.66946 26.8906 5.99224 25.7154L6.7795 24.6543M6.79091 24.4375C6.72246 24.4375 6.654 24.4717 6.61977 24.5288L5.8325 25.5899C5.8325 25.5899 5.78687 25.6926 5.78687 25.7496C5.78687 25.8067 5.8325 25.8637 5.87814 25.8979C7.56677 27.0959 9.54064 27.906 11.5716 28.2597C11.583 28.2597 11.5944 28.2597 11.6058 28.2597C11.6514 28.2597 11.6971 28.2483 11.7427 28.2141C11.7883 28.1799 11.8226 28.1228 11.8226 28.0658L11.9481 26.7422C11.9481 26.6281 11.8796 26.5255 11.7655 26.5026C10.0313 26.1946 8.35404 25.4872 6.91642 24.4831C6.88219 24.4603 6.83655 24.4489 6.79091 24.4489V24.4375Z"
                      fill="#565656"
                    />
                    <path
                      d="M2.69453 22.5315C2.69453 22.5315 2.63748 22.5087 2.62607 22.4858C1.42806 20.7972 0.606564 18.8348 0.241455 16.8038C0.241455 16.7696 0.241455 16.7468 0.264274 16.724C0.287094 16.7012 0.309913 16.6783 0.332732 16.6783L1.64484 16.4844C1.70189 16.4844 1.74753 16.5186 1.74753 16.5757C2.067 18.3556 2.79722 20.0784 3.83549 21.5617C3.86972 21.6073 3.85831 21.6758 3.81267 21.71L2.7744 22.5087C2.7744 22.5087 2.72876 22.5315 2.70594 22.5315C2.70594 22.5315 2.70594 22.5315 2.69453 22.5315Z"
                      fill="#565656"
                    />
                    <path
                      d="M1.64505 16.5976C1.97593 18.389 2.69474 20.1232 3.75584 21.6179L2.71756 22.4166C1.53096 20.7393 0.720871 18.7997 0.355763 16.7802L1.65646 16.5862M1.64505 16.3809C1.64505 16.3809 1.62223 16.3809 1.61082 16.3809L0.310124 16.5748C0.253076 16.5748 0.196027 16.6205 0.161799 16.6661C0.12757 16.7117 0.11616 16.7688 0.12757 16.8372C0.492678 18.8796 1.32558 20.8648 2.5236 22.5649C2.55782 22.6105 2.61487 22.6448 2.67192 22.6562C2.67192 22.6562 2.69474 22.6562 2.70615 22.6562C2.75179 22.6562 2.79743 22.6448 2.84307 22.6105L3.88134 21.8119C3.97262 21.7434 3.99544 21.6065 3.92698 21.5152C2.8887 20.0434 2.18131 18.3433 1.86184 16.5748C1.83902 16.4721 1.74774 16.3923 1.64505 16.3923V16.3809Z"
                      fill="#565656"
                    />
                    <path
                      d="M0.299041 12.2272C0.299041 12.2272 0.241993 12.2044 0.230583 12.1816C0.207764 12.1588 0.207764 12.1245 0.207764 12.1017C0.538643 10.0594 1.32591 8.07411 2.48969 6.38548C2.5011 6.36266 2.53533 6.33984 2.55815 6.33984C2.55815 6.33984 2.56956 6.33984 2.58097 6.33984C2.60379 6.33984 2.62661 6.33984 2.64943 6.36266L3.69912 7.14993C3.69912 7.14993 3.75616 7.25262 3.72193 7.29825C2.70648 8.79292 2.0219 10.5272 1.72525 12.3185C1.72525 12.3755 1.6682 12.4098 1.62256 12.4098L0.31045 12.2386L0.299041 12.2272Z"
                      fill="#565656"
                    />
                    <path
                      d="M2.5694 6.44304L3.61909 7.2303C2.59222 8.73637 1.89623 10.482 1.59958 12.2962L0.298879 12.125C0.629759 10.0941 1.40562 8.13166 2.55799 6.45445M2.5694 6.21484C2.5694 6.21484 2.54658 6.21484 2.53517 6.21484C2.47812 6.21484 2.42107 6.26048 2.38684 6.30612C1.22306 8.02898 0.424386 10.0143 0.0935059 12.0794C0.0935059 12.1364 0.0935059 12.1935 0.139144 12.2505C0.173373 12.2962 0.230422 12.3304 0.28747 12.3418L1.58817 12.513C1.58817 12.513 1.61099 12.513 1.6224 12.513C1.72508 12.513 1.81636 12.4331 1.83918 12.3304C2.12442 10.5505 2.82041 8.83906 3.82446 7.35581C3.89292 7.25312 3.8701 7.12761 3.77882 7.05916L2.72913 6.27189C2.72913 6.27189 2.64927 6.22625 2.60363 6.22625L2.5694 6.21484Z"
                      fill="#565656"
                    />
                    <path
                      d="M6.61931 4.25172C6.61931 4.25172 6.55086 4.24031 6.52804 4.20608L5.72936 3.1564C5.72936 3.1564 5.70654 3.09935 5.70654 3.07653C5.70654 3.0423 5.72936 3.01948 5.75218 3.00807C7.40658 1.78724 9.34622 0.942924 11.3657 0.566406C11.3657 0.566406 11.3771 0.566406 11.3885 0.566406C11.4114 0.566406 11.4342 0.566406 11.457 0.589226C11.4798 0.600635 11.5026 0.634864 11.5026 0.657683L11.6966 1.96979C11.6966 2.02684 11.6624 2.08389 11.6053 2.0953C9.84824 2.43759 8.14821 3.17921 6.68777 4.24031C6.66495 4.25172 6.64213 4.26313 6.61931 4.26313V4.25172Z"
                      fill="#565656"
                    />
                    <path
                      d="M11.3774 0.66929L11.5828 1.96999C9.80287 2.31228 8.09142 3.05391 6.61958 4.12641L5.8209 3.08813C7.4753 1.87871 9.39212 1.04581 11.3888 0.66929M11.3774 0.452507C11.3774 0.452507 11.3546 0.452507 11.3318 0.452507C9.30084 0.840435 7.34979 1.68475 5.67258 2.91699C5.62694 2.95122 5.59271 3.00827 5.5813 3.06531C5.5813 3.12236 5.5813 3.17941 5.62694 3.22505L6.42561 4.26333C6.42561 4.26333 6.5283 4.3432 6.59676 4.3432C6.6424 4.3432 6.68803 4.33179 6.72226 4.29756C8.17129 3.24787 9.85992 2.50624 11.6056 2.17536C11.7197 2.15254 11.7996 2.03845 11.7767 1.92435L11.5714 0.623651C11.5714 0.566603 11.5257 0.509555 11.4801 0.475326C11.4459 0.452507 11.4002 0.429688 11.3546 0.429688L11.3774 0.452507Z"
                      fill="#565656"
                    />
                    <path
                      d="M16.2383 28.1309C16.2383 28.1309 16.1927 28.1309 16.1698 28.1081C16.147 28.0966 16.1242 28.0624 16.1242 28.0396L15.9417 26.7275C15.9417 26.6704 15.9759 26.6134 16.0329 26.602C16.398 26.5335 16.7517 26.4537 17.1054 26.3624L17.3907 26.2711C17.7672 26.157 18.0753 26.0543 18.3719 25.9288C18.5659 25.8604 18.6686 25.8147 18.7712 25.7691C19.0565 25.6436 19.3417 25.4952 19.627 25.3469C19.9008 25.1986 20.1632 25.0503 20.4256 24.8791C20.6652 24.7308 20.7907 24.6509 20.9048 24.5711C24.2593 22.2207 26.256 18.387 26.256 14.3138C26.256 10.2405 24.2935 6.42973 21.0189 4.10216L20.9163 4.0337C20.1974 3.54309 19.4216 3.13234 18.6115 2.79005C18.1209 2.59609 17.733 2.45917 17.3336 2.34507L17.2081 2.31085C16.8316 2.20816 16.4665 2.11688 16.1014 2.04842C16.0443 2.04842 16.0101 1.97997 16.0101 1.92292L16.1927 0.610808C16.1927 0.610808 16.2155 0.55376 16.2383 0.542351C16.2611 0.530941 16.2839 0.519531 16.3068 0.519531C16.3068 0.519531 16.3182 0.519531 16.3296 0.519531C16.7289 0.587989 17.1511 0.690676 17.6189 0.816182L17.7558 0.861821C18.1893 0.987327 18.6343 1.13565 19.0907 1.31821L19.2048 1.36384C24.4304 3.52027 27.8077 8.59756 27.8077 14.3024C27.8077 18.6951 25.6855 22.8938 22.1257 25.5637C22.1257 25.5751 22.023 25.6664 22.0116 25.6778L21.772 25.8261C21.6807 25.8946 21.578 25.9516 21.4867 26.0201L21.3156 26.1228C20.9847 26.3281 20.6424 26.5335 20.3001 26.7161C20.0035 26.8758 19.7068 27.0241 19.3874 27.1611C19.2276 27.2295 19.0679 27.2866 18.9082 27.355C18.5773 27.4805 18.2464 27.606 17.9041 27.7087C17.9041 27.7087 17.5846 27.8114 17.482 27.8342C17.0484 27.9483 16.6376 28.0396 16.2383 28.1081C16.2383 28.1081 16.2269 28.1081 16.2155 28.1081L16.2383 28.1309Z"
                      fill="#565656"
                    />
                    <path
                      d="M16.3178 0.646471C16.7171 0.714929 17.1393 0.817615 17.5956 0.943122L17.7211 0.98876C18.1547 1.11427 18.5883 1.26259 19.0447 1.44515L19.1588 1.49078C24.3387 3.62439 27.6932 8.66745 27.6932 14.338C27.6932 18.6965 25.5824 22.8725 22.0568 25.5081L21.9883 25.5537L21.9427 25.6222L21.7031 25.7705C21.6118 25.839 21.5091 25.896 21.4179 25.9531L21.2467 26.0557C20.9158 26.2611 20.585 26.4665 20.2313 26.6491C19.9346 26.8088 19.638 26.9571 19.3299 27.094C19.1816 27.1625 19.0218 27.2195 18.8621 27.2766C18.5312 27.4021 18.2003 27.5162 17.8695 27.6189C17.8695 27.6189 17.55 27.7216 17.4473 27.7444C17.0137 27.8585 16.6144 27.9498 16.2151 28.0182L16.0325 26.7061C16.3976 26.6376 16.7627 26.5578 17.1164 26.4665L17.4131 26.3752C17.7896 26.2611 18.0977 26.147 18.4057 26.0329C18.5997 25.9531 18.7024 25.9188 18.8051 25.8732C19.1017 25.7477 19.387 25.5994 19.6722 25.451C19.946 25.3027 20.2199 25.143 20.4709 24.9832C20.6762 24.8577 20.8246 24.7665 20.9615 24.6638C24.3387 22.2906 26.3582 18.4227 26.3582 14.3266C26.3582 10.2306 24.3844 6.37411 21.0756 4.03514L20.9729 3.95527H20.9501C20.2427 3.46465 19.4668 3.0425 18.6453 2.70021L18.5426 2.65457C18.1547 2.49483 17.7554 2.35792 17.356 2.24382L17.2305 2.20959C16.854 2.10691 16.4775 2.01563 16.1124 1.94717L16.2949 0.635061M16.3178 0.429688C16.3178 0.429688 16.2265 0.441097 16.1922 0.475326C16.1466 0.509555 16.1124 0.566603 16.101 0.623651L15.9184 1.93576C15.907 2.04986 15.9755 2.16395 16.101 2.18677C16.4661 2.25523 16.8312 2.3351 17.2077 2.43779L17.3218 2.47201C17.7211 2.58611 18.1205 2.72303 18.497 2.87135L18.5997 2.91699C19.4098 3.24787 20.1628 3.67003 20.8588 4.14923C20.8816 4.16064 20.9044 4.17205 20.9273 4.18346L20.9843 4.2291C24.236 6.53385 26.1757 10.3104 26.1757 14.3495C26.1757 18.3885 24.1904 22.1765 20.8702 24.5154C20.7447 24.6067 20.6306 24.6752 20.448 24.7893L20.391 24.8235C20.1286 24.9832 19.8662 25.143 19.6037 25.2913C19.2729 25.4624 19.0104 25.5994 18.7594 25.7021C18.6567 25.7477 18.554 25.7819 18.3715 25.8618C18.0634 25.9873 17.7554 26.09 17.3903 26.2041L17.0936 26.2954C16.7285 26.398 16.3748 26.4779 16.0325 26.535C15.9184 26.5578 15.8386 26.6605 15.85 26.786L16.0325 28.0981C16.0325 28.1551 16.0667 28.2122 16.1238 28.2464C16.158 28.2692 16.2037 28.292 16.2493 28.292C16.2607 28.292 16.2721 28.292 16.2835 28.292C16.6943 28.2236 17.0936 28.1323 17.5386 28.0068C17.6413 27.984 17.9607 27.8813 17.9607 27.8813C18.303 27.7786 18.6453 27.6645 18.9648 27.5276C19.1245 27.4705 19.2843 27.4021 19.444 27.3336C19.7635 27.1967 20.0601 27.0484 20.3568 26.8887C20.6762 26.7175 21.0185 26.5235 21.3836 26.2839L21.5548 26.1813C21.6575 26.1242 21.7487 26.0557 21.8514 25.9873L22.0682 25.8504C22.0682 25.8504 22.1253 25.8047 22.1367 25.7819L22.1595 25.7477L22.1937 25.7249C25.7764 23.0436 27.91 18.7992 27.91 14.3837C27.91 8.63322 24.5099 3.51029 19.2386 1.34246L19.1245 1.29682C18.6681 1.11427 18.2232 0.965941 17.7782 0.829025L17.6527 0.794796C17.1735 0.65788 16.7513 0.566603 16.352 0.486736C16.3406 0.486736 16.3292 0.486736 16.3178 0.486736V0.429688Z"
                      fill="#565656"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_15218_75146">
                      <rect
                        width="27.8167"
                        height="27.8167"
                        fill="white"
                        transform="translate(0.0935059 0.431641)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <span>
                  {speed === 'normal'
                    ? '1.0x'
                    : speed === 'fast'
                      ? '1.2x'
                      : '1.5x'}
                </span>
              </button>
              <div
                className={style.options}
                data-show={showSpeedMenu}
                data-locale={locale}
              >
                <div className={style.label}>再生速度</div>
                <div
                  className={style.option}
                  onClick={() => changeSpeed('normal')}
                >
                  1.0x
                </div>
                <div
                  className={style.option}
                  onClick={() => changeSpeed('fast')}
                >
                  1.2x
                </div>
                <div
                  className={style.option}
                  onClick={() => changeSpeed('x-fast')}
                >
                  1.5x
                </div>
              </div>
            </div>
          </div>

          <div className={style.list} ref={playlistRef}>
            {playArticles.map((article, index) => (
              <button
                key={index}
                className={style.item}
                data-active={voice.playIndex === index}
                onClick={() => changeIndex(index)}
              >
                <div className={style.playContainer}>
                  {voice.playIndex === index ? (
                    <img src={playingBlackGif} alt="playing" />
                  ) : (
                    <PlayIcon />
                  )}
                </div>
                <div className={style.name}>{article.title}</div>
              </button>
            ))}
          </div>

          <audio
            ref={audioRef}
            title={currentArticle?.title}
            src={buildVoiceUrl(currentArticle.blogId, locale, speed)}
            onEnded={onEnd}
            onLoadedData={() => setIsLoading(false)}
            onTimeUpdate={updateProgress}
            onLoadedMetadata={updateProgress}
          />
        </>
      )}
    </div>
  )
}

export default VoicePlayer
